import type { FieldValues, UseFormReturn, Path } from 'react-hook-form';

import type { ReactElement, ReactNode, Ref } from 'react';
import {
  forwardRef,
  Children,
  createElement,
  isValidElement,
  Fragment
} from 'react';

import { Form as FormValidation } from '../../lib/form/Form';
import clsx from 'clsx';

import { ErrorTextForm } from '../../atoms/ErrorTextForm';
import { Typography } from '../../atoms/Typography';
import { Button } from '../../molecules/buttons';
import { Input } from '../Input';
import { MaskedInput } from '../MaskedInput';
import { Select } from '../Select';
import { TextArea } from '../TextArea';

type FooterProps = {
  children?: ReactNode;
  className?: string;
  horizontalAligment?: 'start' | 'center' | 'end';
};

type ErrorProps = {
  className?: string;
  errormessage?: string;
};

type ItemProps = {
  children?: ReactElement | ReactElement[];
  className?: string;
  name?: Path<FieldValues>;
  errorMessage?: 'string';
  disabled?: boolean;
};

type LabelProps = {
  children?: ReactNode;
  label?: string;
  name?: string;
  required?: boolean;
  colon?: boolean;
  htmlFor?: string;
  className?: string;
};

type FormProps<TFieldValues extends FieldValues> = {
  children: ReactElement | ReactElement[];
  className?: string;
  methods: UseFormReturn<TFieldValues>;
  loggingMode?: 'always' | 'only first render' | 'never';
  onSubmit?: () => void;
};

type DivProps = {
  children?: ReactElement | ReactElement[];
  name?: string;
  className?: string;
};

type AdditionalProps = Record<string, unknown>;

function processChildren(
  children: ReactNode,
  additionalProps: AdditionalProps
): ReactNode {
  return Children.map(children, (child) => {
    if (isValidElement(child) && child.type !== Fragment) {
      const { type, props } = child as ReactElement;

      if (typeof props === 'object' && props !== null) {
        return createElement(type, {
          ...props,
          ...additionalProps
        });
      }
    }

    return child;
  });
}

const Footer = ({
  children,
  className,
  horizontalAligment = 'end'
}: FooterProps) => (
  <div
    className={clsx(
      className,
      !className && 'flex flex-row gap-2 text-sm w-full',
      `justify-${horizontalAligment}`
    )}
  >
    {children}
  </div>
);

const Error = ({ className, errormessage }: ErrorProps) => {
  if (!errormessage) return <></>;

  return (
    <div className={className}>
      <ErrorTextForm message={errormessage} />
    </div>
  );
};

const Item = forwardRef(
  (
    {
      children,
      className,
      name,
      errorMessage: errormessage,
      ...rest
    }: ItemProps,
    ref: Ref<HTMLDivElement>
  ) => (
    <div
      ref={ref}
      className={clsx(
        className,
        !className && 'flex flex-col items-start gap-2 text-sm w-full'
      )}
    >
      {children ? (
        processChildren(children, { name, errormessage, ...rest })
      ) : (
        <></>
      )}
    </div>
  )
);

Item.displayName = 'Item';

const Label = ({
  required,
  colon = false,
  label,
  name,
  htmlFor,
  children,
  className
}: LabelProps) => (
  <label
    className={clsx(className, !className && 'flex items-start gap-1')}
    htmlFor={htmlFor ?? name}
  >
    {required ? (
      <Typography.Text className={'text-red-400'}>{'*'}</Typography.Text>
    ) : (
      <></>
    )}
    <span>
      {label || children}
      {colon ? <Typography.Text>{':'}</Typography.Text> : <></>}
    </span>
  </label>
);

const Form = <TFieldValues extends FieldValues>({
  children,
  methods,
  loggingMode,
  className,
  onSubmit
}: FormProps<TFieldValues>) => (
  <form
    className={clsx(className, !className && 'flex flex-col w-full gap-5')}
    onSubmit={onSubmit}
  >
    <FormValidation methods={methods} loggingMode={loggingMode}>
      {children}
    </FormValidation>
  </form>
);

const Column = forwardRef(
  ({ children, className, ...rest }: DivProps, ref: Ref<HTMLDivElement>) => (
    <div
      ref={ref}
      {...rest}
      className={clsx('flex flex-col w-full', className)}
    >
      {children ? processChildren(children, { ...rest }) : <></>}
    </div>
  )
);

Column.displayName = 'Column';

const Row = forwardRef(
  (
    { children, name, className, ...rest }: DivProps,
    ref: Ref<HTMLDivElement>
  ) => (
    <div ref={ref} {...rest} className={clsx('flex flex-row', className)}>
      {children ? processChildren(children, { name, ...rest }) : <></>}
    </div>
  )
);

Row.displayName = 'Row';

Form.Item = Item;

Form.Label = Label;

Form.Input = Input;

Form.TextArea = TextArea;

Form.MaskedInput = MaskedInput;

Form.Select = Select;

Form.Footer = Footer;

Form.Button = Button;

Form.Error = Error;

Form.Column = Column;

Form.Row = Row;

export { Form };
export type { FormProps };
